import { GatewayStatus } from 'constants/gatewayStatuses';
import { OperationAccess } from 'constants/operationAccesses';
import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { StyledChip } from 'pages/Gateways/GatewaysShow/GatewaysShow.styled';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord, useRecordContext } from 'react-admin';
import { Box } from 'shared/mui/Box';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Chip } from 'shared/mui/Chips';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import { Typography } from 'shared/mui/Typography';
import { BooleanField, FunctionField, TextField } from 'shared/react-admin/Fields';
import { EmbeddedArrayField } from 'shared/react-admin/Fields/Embedded/EmbeddedArrayField/EmbeddedArrayField';
import { Currency } from 'types/embedded/currency';
import { Gateway, GatewayMethod } from 'types/embedded/gateway';

export const GatewayShowInfoTable = () => {
  const translate = useTypedTranslate();

  const record = useRecordContext<Gateway>();
  const { hasResourceAccess, hasOperationAccess } = usePermissions();

  return (
    <CardTable tableBodyProps={{ sx: { '& td': { width: '233px' } } }}>
      <TableRowWrapper label={translate('app.gateway.externalAlias')}>
        <TextField source="externalAlias" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.alias')}>
        <TextField source="alias" />
      </TableRowWrapper>
      {hasOperationAccess(OperationAccess.ManageMerchantAccounts) && (
        <TableRowWrapper label="Проверен менеджером">
          <BooleanField source="managerVerified" />
        </TableRowWrapper>
      )}
      {record?.status?.value === GatewayStatus.Disabled && (
        <TableRowWrapper label={translate('app.gateway.deactivationReason')}>
          <TextField source="deactivationReason.reason" />
        </TableRowWrapper>
      )}
      <TableRowWrapper label="PCI DSS">
        <BooleanField source="pcidss" />
      </TableRowWrapper>
      <TableRowWrapper label="Push Api">
        <BooleanField source="pushApi" />
      </TableRowWrapper>
      {record?.status?.value !== GatewayStatus.Pilot && hasResourceAccess(Resources.Methods) && (
        <TableRowWrapper label={translate('app.filter.methods')}>
          <EmbeddedArrayField
            render={(values: GatewayMethod[]) => (
              <Box display="flex" flexWrap="wrap" gap="4px">
                {values.map((v: GatewayMethod) => {
                  return <Chip key={v.id} label={v.method.name} />;
                })}
              </Box>
            )}
            sourceField="gatewayMethods"
          >
            <FunctionField render={(record: RaRecord) => <StyledChip label={record.name} />} />
          </EmbeddedArrayField>
        </TableRowWrapper>
      )}
      {hasResourceAccess(Resources.Currencies) && (
        <TableRowWrapper label={translate('app.gateway.defaultCurrency')}>
          <TextField source="defaultCurrency.alphaCode" />
        </TableRowWrapper>
      )}
      {hasResourceAccess(Resources.Currencies) && (
        <TableRowWrapper label={translate('app.gateway.supportedCurrencies')}>
          <EmbeddedArrayField
            render={(values: Currency[]) => {
              return values.map((v: Currency) => {
                return (
                  <Chip
                    key={v['@id']}
                    label={v.alphaCode}
                    sx={{
                      marginLeft: '0!important',
                      marginRight: '8px!important',
                    }}
                  />
                );
              });
            }}
            sourceField="supportedCurrencies"
          ></EmbeddedArrayField>
        </TableRowWrapper>
      )}
      <TableRowWrapper label={translate('app.gateway.orderExpirationTimeSec')}>
        <TextField source="orderExpirationTimeSec" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.gateway.saveCustomerMethodData')}>
        <BooleanField source="saveCustomerMethodData" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.gateway.statusRequestEnabled')}>
        <BooleanField source="statusRequestEnabled" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.gateway.transactionRequestMaxAttempts')}>
        <TextField source="transactionRequestMaxAttempts" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.gateway.statusPollingInterval')}>
        <FunctionField
          render={(record: Gateway) => {
            return (
              <Typography component="span" variant="body2">
                {record?.attemptIntervalConfig?.join(', ')}
              </Typography>
            );
          }}
        />
      </TableRowWrapper>
    </CardTable>
  );
};
